import { EventTracker } from '@/utils';
import { Button } from '@design-system/Button';
import useStorifyConfig from '@hooks/useStorifyConfig';
import { IKitType, KitType } from '@viome/viome-base/src/usecase/Bootup/BootupModel';
import { useEffect, useRef, useState } from 'react';

interface StorifyMeProps
  extends React.DetailedHTMLProps<
    React.HTMLAttributes<HTMLElement>,
    HTMLElement
  > {
  account: string;
  widget: string;
  story?: string;
  env: 'eu';
  segments?: string[];
  playbackOptions?: 'restartStoriesWhenOpen' | 'alwaysResumeStoryWhereStopped'; // default: 'alwaysResumeStoryWhereStopped'
  logLevel?: 'all' | 'info' | 'warning' | 'error' | 'none'; // default: 'none'
  init?: 'auto' | 'manual';
  reorderStories?: 'onClose' | undefined;
}

declare global {
  namespace JSX {
    interface IntrinsicElements {
      'storifyme-collection': StorifyMeProps;
    }
  }
}

interface HealthResultCardProps {
  kitType?: IKitType;
}

type KitConfig = {
  title: string;
  description: string;
  buttonText?: string;
  buttonAction: 'storify';
  showStorify: boolean;
  buttonWidth?: string;
  staticMessage?: string;
};

// Default configuration
const DEFAULT_CONFIG: KitConfig = {
  title: "Your health insights and recommendations are ready!",
  description: "It's time to see your health in a whole new light.",
  buttonText: "Start here",
  buttonAction: 'storify',
  showStorify: true,
  buttonWidth: 'w-24',
};

// Special configurations
const SPECIAL_CONFIGS: Partial<Record<IKitType, KitConfig>> = {
  [KitType.PHPRO]: {
    title: "Your Precision Health PRO™ results are ready!",
    description: "Provided by Viome in partnership with your healthcare provider.",
    buttonAction: 'storify',
    showStorify: false,
    staticMessage: "To review your full report, please consult with your healthcare provider.",
  }
};

const getKitConfig = (kitType: IKitType | null | undefined) => {
  if (!kitType) return DEFAULT_CONFIG;
  return SPECIAL_CONFIGS[kitType] || DEFAULT_CONFIG;
};

export const HealthResultCard = ({ kitType }: HealthResultCardProps) => {
  const [showResults, setShowResults] = useState(false);
  const { tags, isStorifyEnabled, storifyWidgetId } = useStorifyConfig(true);

  const config = getKitConfig(kitType);

  const handleStartHere = () => {
    EventTracker.trackEvent('Home', 'home_hero_results_ready_start_here');
    setShowResults(true);
  };

  const storifymeRef = useRef(null);

  useEffect(() => {
    if (showResults && storifymeRef.current) {
      const storifymeElement = storifymeRef?.current;

      const handleReady = () => {
        // Set init to "manual"
        storifymeElement?.setAttribute('init', 'manual');

        storifymeElement?.openWidgetStoryByPosition(1);
      };

      storifymeElement.addEventListener('onLoad', handleReady);

      storifymeElement?.addEventListener('onStoryClosed', () => {
        setShowResults(false);
      });

      return () => {
        storifymeElement.removeEventListener('ready', handleReady);
      };
    }
    return;
  }, [showResults]);

  return (
    <>
      <div className={`w-full p-8 rounded-xl bg-overlay-primary backdrop-blur-[50px] flex flex-col ${config.staticMessage ? 'gap-3' : 'h-60 justify-between'}`}>
        <div className="space-y-2">
          <h2 className="typography-h1 text-white font-sans">
            {config.title}
          </h2>
          <p className="typography-body text-white font-light">
            {config.description}
          </p>
        </div>

        {config.staticMessage ? (
          <p className="typography-body text-white font-light italic">
            {config.staticMessage}
          </p>
        ) : (
          <Button
            variant="secondary"
            className={`typography-caption1 h-9 bg-white text-primary ${config.buttonWidth ?? ''}`}
            onClick={handleStartHere}
          >
            {config.buttonText ?? ''}
          </Button>
        )}
      </div>
      {showResults && isStorifyEnabled && config.showStorify && (
        <div className="hidden">
          <storifyme-collection
            ref={storifymeRef}
            className="ml-6 hidden"
            account="viome-4593429"
            widget={storifyWidgetId}
            env="eu"
            playbackOptions="restartStoriesWhenOpen"
            segments={tags}
          />
        </div>
      )}
    </>
  );
};
